import React from 'react'
import { Head, Link } from '@inertiajs/react'
import { Plus, Edit3, Trash2, Eye, EyeOff, Clock, FileText } from 'lucide-react'
import { AdminLayout } from '@/components/admin/templates/AdminLayout'
import { Surface } from '@/components/admin/atoms/layout/Surface'
import { Stack } from '@/components/admin/atoms/layout/Stack'
import { Inline } from '@/components/admin/atoms/layout/Inline'
import { Grid } from '@/components/admin/atoms/layout/Grid'
import { Padding } from '@/components/admin/atoms/layout/Padding'
import { ScrollArea } from '@/components/admin/atoms/layout/ScrollArea'
import { Heading } from '@/components/admin/atoms/typography/Heading'
import { Text } from '@/components/admin/atoms/typography/Text'
import { Muted } from '@/components/admin/atoms/typography/Muted'
import { Badge } from '@/components/admin/atoms/feedback/Badge'
import { EmptyState } from '@/components/admin/atoms/feedback/EmptyState'
import { IconButton } from '@/components/admin/atoms/actions/IconButton'
import { StatFilterButton } from '@/components/admin/atoms/actions/StatFilterButton'
import { LinkButton } from '@/components/admin/atoms/actions/LinkButton'
import { SearchInput } from '@/components/admin/molecules/data/SearchInput'
import { Pagination } from '@/components/admin/molecules/data/Pagination'
import { StatCard } from '@/components/admin/molecules/data/StatCard'
import { ConfirmDialog } from '@/components/admin/organisms/ConfirmDialog'
import { SelectInput } from '@/components/admin/atoms/inputs/SelectInput'
import { DataTable } from '@/components/admin/atoms/data/DataTable'
import { DataTableHead } from '@/components/admin/atoms/data/DataTableHead'
import { DataTableHeaderCell } from '@/components/admin/atoms/data/DataTableHeaderCell'
import { DataTableBody } from '@/components/admin/atoms/data/DataTableBody'
import { DataTableRow } from '@/components/admin/atoms/data/DataTableRow'
import { DataTableCell } from '@/components/admin/atoms/data/DataTableCell'
import { formatDate } from '@/lib/utils'
import { usePostsIndexVM } from './Index.vm'
import type { PageProps } from '@/types/inertia.d'
import type { AdminPost } from '@/types/admin'
import type { PaginationMeta } from '@/types/shared'

interface PostStats {
  totalPosts: number
  publishedPosts: number
  draftPosts: number
  scheduledPosts: number
}

type Props = PageProps<{
  posts: AdminPost[]
  pagination: PaginationMeta
  filters: { status: string | null; search: string | null; category_id: string | null }
  stats: PostStats
  categories: { id: number; name: string; slug: string }[]
}>

const statusVariant = (status: string): 'success' | 'warning' | 'ghost' => {
  if (status === 'published') return 'success'
  if (status === 'draft') return 'warning'
  return 'ghost'
}

const PostsIndex: React.FC<Props> = ({ posts, pagination, filters, stats, categories }) => {
  const vm = usePostsIndexVM(filters)

  return (
    <AdminLayout breadcrumbs={[{ label: 'Posts' }]}>
      <Head title="Posts" />
      <Stack gap="6">
        <Grid cols="4" gap="4">
          {[
            { label: 'Total Posts', value: stats.totalPosts, icon: <FileText style={{ width: 20, height: 20 }} />, filter: undefined, active: !vm.statusFilter, color: 'primary' as const },
            { label: 'Published', value: stats.publishedPosts, icon: <Eye style={{ width: 20, height: 20 }} />, filter: 'published', active: false, color: 'success' as const },
            { label: 'Drafts', value: stats.draftPosts, icon: <EyeOff style={{ width: 20, height: 20 }} />, filter: 'draft', active: false, color: 'warning' as const },
            { label: 'Scheduled', value: stats.scheduledPosts, icon: <Clock style={{ width: 20, height: 20 }} />, filter: 'scheduled', active: false, color: 'info' as const },
          ].map(({ label, value, filter, icon, active, color }) => (
            <StatFilterButton
              key={label}
              active={active || vm.statusFilter === filter}
              onClick={() => filter ? vm.handleStatusChange(filter) : vm.handleStatusChange('')}
            >
              <StatCard
                label={label}
                value={value}
                icon={icon}
                color={color}
              />
            </StatFilterButton>
          ))}
        </Grid>

        <Surface variant="card" radius="lg">
          <Padding all="6">
            <Stack gap="4">
              <Inline justify="between" align="center">
                <Heading level={4}>Posts Management</Heading>
                <LinkButton href="/admin/posts/new" variant="primary" size="sm">
                  <Plus style={{ width: 16, height: 16 }} />
                  New Post
                </LinkButton>
              </Inline>

              <Grid cols="3" gap="4">
                <SearchInput
                  value={vm.search}
                  onChange={vm.setSearch}
                  placeholder="Search posts..."
                />
                <SelectInput
                  value={vm.statusFilter}
                  onChange={(val) => vm.handleStatusChange(val)}
                  options={[
                    { value: '', label: 'Filter by Status' },
                    { value: 'published', label: 'Published' },
                    { value: 'draft', label: 'Draft' },
                  ]}
                />
                <SelectInput
                  value={vm.categoryFilter}
                  onChange={(val) => vm.handleCategoryChange(val)}
                  options={[
                    { value: '', label: 'Filter by Category' },
                    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
                  ]}
                />
              </Grid>

              {posts.length === 0 ? (
                <EmptyState
                  title="No posts found"
                  description={vm.statusFilter || vm.search ? 'Try adjusting your filters.' : 'Create your first post to get started.'}
                  action={
                    <LinkButton href="/admin/posts/new" variant="primary" size="sm">
                      New Post
                    </LinkButton>
                  }
                />
              ) : (
                <ScrollArea horizontal>
                  <DataTable>
                    <DataTableHead>
                      <DataTableHeaderCell>Title</DataTableHeaderCell>
                      <DataTableHeaderCell>Category</DataTableHeaderCell>
                      <DataTableHeaderCell>Tags</DataTableHeaderCell>
                      <DataTableHeaderCell>Status</DataTableHeaderCell>
                      <DataTableHeaderCell>Date</DataTableHeaderCell>
                      <DataTableHeaderCell align="right" width="96px">Actions</DataTableHeaderCell>
                    </DataTableHead>
                    <DataTableBody>
                      {posts.map((post) => (
                        <DataTableRow key={post.id}>
                          <DataTableCell>
                            <Stack gap="0">
                              <Text size="sm" weight="medium">{post.title}</Text>
                              <Text size="xs" color="muted">/blog/{post.slug}</Text>
                            </Stack>
                          </DataTableCell>
                          <DataTableCell>
                            <Text size="sm" color="muted">{post.category?.name ?? '---'}</Text>
                          </DataTableCell>
                          <DataTableCell>
                            <Inline gap="1" wrap>
                              {post.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag.id} variant="info">{tag.name}</Badge>
                              ))}
                              {post.tags.length > 3 && (
                                <Muted>+{post.tags.length - 3}</Muted>
                              )}
                            </Inline>
                          </DataTableCell>
                          <DataTableCell>
                            <Badge variant={statusVariant(post.status)}>{post.status}</Badge>
                          </DataTableCell>
                          <DataTableCell>
                            <Text size="sm" color="muted">{formatDate(post.created_at)}</Text>
                          </DataTableCell>
                          <DataTableCell align="right">
                            <Inline gap="1" justify="end">
                              <Link href={`/admin/posts/${post.id}/edit`}>
                                <IconButton
                                  icon={<Edit3 style={{ width: 16, height: 16 }} />}
                                  label="Edit"
                                  variant="ghost"
                                />
                              </Link>
                              <IconButton
                                icon={<Trash2 style={{ width: 16, height: 16 }} />}
                                label="Delete"
                                variant="error"
                                onClick={() => vm.setDeleteId(post.id)}
                              />
                            </Inline>
                          </DataTableCell>
                        </DataTableRow>
                      ))}
                    </DataTableBody>
                  </DataTable>
                </ScrollArea>
              )}

              <Pagination meta={pagination} onPageChange={vm.handlePageChange} />
            </Stack>
          </Padding>
        </Surface>
      </Stack>

      <ConfirmDialog
        open={vm.deleteDialogOpen}
        title="Delete Post"
        description="This will permanently delete the post. This action cannot be undone."
        onConfirm={vm.handleDelete}
        onCancel={() => vm.setDeleteId(null)}
        loading={vm.deleting}
      />
    </AdminLayout>
  )
}

export default PostsIndex
